<template>
  <aha-section>
    <template #header>
      <h5>{{ $i18nPresenter.t('Current plan') }}</h5>
    </template>
    <template #body>
      <h2 class="font-bold text-2xl mb-2">
        {{ $i18nPresenter.t('Free') }}
      </h2>
      <table class="table-plan">
        <colgroup>
          <col class="w-1/2 md:w-1/3">
          <col class="w-1/2 md:w-2/3">
        </colgroup>
        <tbody class="w-full">
          <tr>
            <td class="table-plan-property">
              <i class="mr-1 fas fa-user fa-fw" />
              {{ $i18nPresenter.t('Owner:') }}
            </td>
            <td class="py-2 table-plan-value">
              {{ currentUser.email }}
            </td>
          </tr>
          <tr>
            <td class="table-plan-property">
              <i class="mr-1 fas fa-users fa-fw" />
              {{ $i18nPresenter.t('Participant limit:') }}
            </td>
            <td class="py-2 table-plan-value">
              {{ planLimit | formatNumber }}
            </td>
          </tr>
        </tbody>
      </table>
      <div class="flex flex-col">
        <div>
          <AhaInfoBox
            class="inline-block"
            v-if="isValidPromoCode"
          >
            <span class=" text-sm text-secondary-100">
              {{ $i18nPresenter.t('Click Upgrade today and save on any account plan! Don’t miss out—offer ends soon!') }}
            </span>
          </AhaInfoBox>
        </div>
        <div>
          <aha-button class="mt-3 " @click="scrollToPlanUpgradePlan">
            {{ $i18nPresenter.t('Upgrade') }}
          </aha-button>
        </div>
      </div>
      <hr class="mt-7 mb-7 text-base-30" />
      <table class="table-plan">
        <tbody class="w-full">
          <colgroup>
            <col class="w-1/2 md:w-1/3">
            <col class="w-1/2 md:w-2/3">
          </colgroup>
          <h3 class="font-bold text-lg mb-2">
            {{ $i18nPresenter.t('Billing & Payment') }}
          </h3>
          <tr>
            <td class="table-plan-property">
              <i class="mr-1 far fa-credit-card fa-fw" />
              {{ $i18nPresenter.t('Payment method:') }}
            </td>
            <td v-if="cardInfo">
              <div class="col">
                <div class="mb-2">
                  <strong class="uppercase mb-2">
                    {{ cardInfo }}
                  </strong>
                </div>
              </div>
            </td>
            <td v-else>
              <span
                @click="redirectToCustomerPortal('add_card')"
                class="text-link cursor-pointer"
                v-if="!isCreatingCustomerPortal"
              >
                {{ $i18nPresenter.t('Add') }}
              </span>
              <div v-else :style="'width:20px'">
                <aha-spinner />
              </div>
            </td>
          </tr>
          <tr>
            <td class="table-plan-property">
              <aha-icon name="coin" class="-mt-0.5 -ml-0.5" />
              {{ $i18nPresenter.t('Credit balance:') }}
            </td>
            <td
              class="py-2 table-plan-value"
            >
              <span v-if="currentUser.balance">
                {{ (0 - parseFloat(currentUser.balance)) | currencyFormat(getCurrency) }} {{ getCurrency }}
              </span>
              <span v-else>{{ 0 | currencyFormat(getCurrency) }} {{ getCurrency }}</span>
            </td>
          </tr>
          <tr>
            <td class="table-plan-property">
              <i class="mr-1 fas fa-address-card fa-fw" />
              {{ $i18nPresenter.t('Billing info:') }}
            </td>
            <td>
              <div
                v-if="billingInfo"
                class="table-plan-property__address mb-2"
              >
                <span v-if="billingInfo.customerName">
                  <PreLine :text="billingInfo.customerName" /> <br />
                </span>
                <span v-if="billingInfo.line1">
                  <PreLine :text="billingInfo.line1" /> <br />
                </span>
                <span v-if="billingInfo.line2">
                  <PreLine :text="billingInfo.line2" /> <br />
                </span>
                <span v-if="billingInfo.city">
                  <PreLine :text="billingInfo.city" />
                </span>
                <span v-if="billingInfo.state">
                  <PreLine :text="`, ${billingInfo.state}`" />
                </span>
                <span v-if="billingInfo.postal_code">
                  <PreLine :text="` ${billingInfo.postal_code}`" />
                </span>

                <span v-if="billingInfo.country"><PreLine :text="` ${billingInfo.country}`" /></span>

                <br />
              </div>
              <div v-else>
                <PreLine :text="currentUser.firstName" /> <br />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <aha-button
        :variant="'base'"
        :disabled="isCreatingCustomerPortal"
        class=" mt-4 mb-3"
        @click="redirectToCustomerPortal('manage')"
      >
        {{ $i18nPresenter.t("Manage") }}
        <aha-spinner class="ml-1" v-if="isCreatingCustomerPortal" />
      </aha-button>
    </template>
  </aha-section>
</template>
<script  >
import { mapState, mapActions, mapGetters } from 'vuex'
import PreLine from '@/components/UI/PreLine.vue'
import utils from '@/util'
import PaymentLocalizationMixin from '@/components-v2/my-plan/user-pricing-v2/PaymentLocalizationMixin'

export default {
  name: 'FreePlanSection',
  components: { PreLine },
  mixins: [PaymentLocalizationMixin],
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters(['planLimit']),
    ...mapState({
      isCreatingCustomerPortal: (state) => state.isCreatingCustomerPortal,
      plan: (state) => state.plan,
      currentUser: (state) => state.currentUser,
      promotionCode: (state) => state.promotionCode,
      isValidPromoCode: (state) => state.isValidPromoCode,
    }),

    getCurrency() {
      if (this.currentUser.currency === 'eur') return 'EUR'
      return this.fullSymbolCurrency
    },

    billingInfo() {
      return this.data.address
    },
    cardInfo() {
      if (!this.data || !this.data.card) return null
      return `${this.data.card.brand} •••• •••• •••• ${this.data.card.last4}`
    },
  },

  methods: {
    ...mapActions(['createCustomerPortalAction']),
    scrollToPlanUpgradePlan() {
      const pricingCompareDom = document.getElementById('upgrade-your-plan')
      pricingCompareDom.scrollIntoView({ behavior: 'smooth' })
    },
    async redirectToCustomerPortal(action) {
      this.createCustomerPortalAction()
      utils.mixpanelTracking('My Plan',
        action === 'add_card' ? 'Billing & Payment > Payment method > Add'
          : 'Billing & Payment > Manage', null, {
          stripeId: this.currentUser.stripeId,
        })
    },
    getPlanColor(subscription) {
      this.$emit('getPlanColor', subscription)
    },
  },
}
</script>
<style lang="scss" scoped>
.text-link{
  color: var(--primary-90, #348EF6);
  font-weight: 700;
}
</style>
